import { SeriesLineOptions } from "highcharts";
import { subMinutes, addMinutes } from "date-fns";
import { useSelector } from "react-redux";
import { State as ReduxState } from "store";
import { THEME_DEFAULT } from "store/userMeta";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import {
  InsightChartMetaFieldsFragment,
  LensTheme,
  LocationScope,
  PriorityFieldsFragment,
  useChartsQuery,
  useInsightsPriorityQuery,
  useRoomQuery
} from "generated/graphql";
import { COLORS, Topic, topicIds } from "Constants";
import {
  addTypeToComboChartSeries,
  createHorizontalThresholdSeries,
  getRangeFromAreaSeries,
  SeriesComboOptions,
  mapAreaChartSeries,
  colorsForChart
} from "utilities/chart";
import { ChartOptions } from "./useAnalysisChartsReducer";

type AnalysisChartsData = {
  chartSeries?: SeriesComboOptions[];
  chartMeta?: InsightChartMetaFieldsFragment;
  topicIndicator?: PriorityFieldsFragment;
  theme: LensTheme;
  colors: string[];
  fetching: boolean;
};

/**
 * UTC dates are expected for query inputs. For simplicity (and due to limitations
 * with date-fns) we use local dates as if they are UTC when selecting a timeStart
 * value. This function makes the necessary adjustment before making the queries.
 */
export const removeTimezoneOffset = (date: Date) =>
  subMinutes(date, date.getTimezoneOffset());

export const addTimezoneOffset = (date: Date) =>
  addMinutes(date, date.getTimezoneOffset());

export const useAnalysisChartsData = (
  locationId: string,
  locationScope: LocationScope,
  options: ChartOptions
): AnalysisChartsData => {
  const { topic, timeScope, timeStart } = options;

  const tenantId = useTenantId();

  // Get Saga Data for the chart from GraphQL
  const { data: chartsData, fetching: isFetchingChartsData } = useChartsQuery({
    variables: {
      tenantId: [tenantId],
      id: locationId,
      locationScope,
      topic: topicIds[topic],
      timeScope,
      timeStart: removeTimezoneOffset(timeStart)
    }
  });

  // Get Saga Data for the topic indicator from GraphQL
  const {
    data: indicatorData,
    fetching: isFetchingIndicatorData
  } = useInsightsPriorityQuery({
    variables: {
      tenant: [tenantId],
      input: {
        device: locationScope === LocationScope.Device ? locationId : undefined,
        room: locationScope === LocationScope.Room ? locationId : undefined,
        site: locationScope === LocationScope.Site ? locationId : undefined,
        locationScope,
        topic: [topicIds[topic]],
        timeScope,
        timePeriodStart: removeTimezoneOffset(timeStart),
        threshold: 0
      }
    }
  });

  const topicIndicator = indicatorData?.topicIndicators.edges[0]?.node;

  // Flag to only run room query and modify series if feature flag is enabled, location scope is room, and topic is average headcount
  const showRoomCapacity =
    locationScope === LocationScope.Room && topic === Topic.MEETING_SIZE;

  // Get room capacity from Prospector
  const { data: roomData, fetching: isFetchingRoomCapacity } = useRoomQuery({
    variables: {
      id: locationId || ""
    },
    pause: !showRoomCapacity
  });

  // Get room capacity value
  const roomCapacity = roomData?.room?.capacity;

  const theme = useSelector<ReduxState, LensTheme>(
    ({ userMeta }) => userMeta?.theme ?? THEME_DEFAULT
  );
  const colors = colorsForChart(
    "area",
    chartsData?.charts?.series.length,
    theme
  );

  const fetching =
    isFetchingChartsData || isFetchingIndicatorData || isFetchingRoomCapacity;

  if (fetching) {
    // Return fetching = true, all others undefined
    return { fetching, theme, colors };
  }

  // Check chart data type is insight chart data
  if (chartsData?.charts?.meta.__typename !== "InsightChartMeta") {
    // Return fetching as false, chart series as undefined
    return { fetching, theme, colors };
  }

  // generate combo chart series data
  const newSeries = mapAreaChartSeries(chartsData.charts.series, theme);

  // add capacity line to combo chart series if applicable
  let updatedSeries: SeriesComboOptions[];

  if (showRoomCapacity && roomCapacity) {
    // build list of all x-axis values for data-points in the area series
    const areaRangeArray: number[] = getRangeFromAreaSeries(newSeries);
    // create a highcharts line series for room capacity
    const roomCapacitySeries: SeriesLineOptions = createHorizontalThresholdSeries(
      areaRangeArray,
      roomCapacity,
      {
        seriesName: "Room Capacity",
        seriesColor: COLORS.THRESHOLD_RED
      }
    );
    // Update area chart series to specify their type as 'area' in the series data
    updatedSeries = addTypeToComboChartSeries(newSeries, "area");
    updatedSeries.push(roomCapacitySeries);
  } else {
    updatedSeries = newSeries;
  }

  return {
    chartSeries: updatedSeries,
    chartMeta: chartsData.charts.meta,
    topicIndicator,
    theme,
    colors,
    fetching
  };
};
